import React, { useState } from "react";
import { useContent } from "../context/ContentContext";
import { resolveImage } from "../lib/api";
import { Reveal } from "../components/Reveal";
import RichText from "../components/RichText";

const PersonCard = ({ p, i }) => {
  const [err, setErr] = useState(false);
  const showImg = p.imageUrl && !err;
  return (
    <Reveal delay={(i % 3) * 0.08} className="group" data-testid="family-person">
      <div className="relative aspect-[4/5] overflow-hidden rounded-lg mb-5 shadow-[0_16px_40px_rgba(38,62,76,0.1)] bg-secondary/50 flex items-center justify-center">
        {showImg ? (
          <img
            src={resolveImage(p.imageUrl)}
            alt={p.name}
            loading="lazy"
            onError={() => setErr(true)}
            className="w-full h-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-105"
          />
        ) : (
          <span className="font-serif text-6xl text-navy/35 select-none" aria-hidden="true">
            {(p.name || "•").trim().charAt(0) || "•"}
          </span>
        )}
      </div>
      <h3 className="font-serif text-2xl text-navy">{p.name}</h3>
      <p className="uppercase tracking-[0.15em] text-xs font-bold text-dusty mt-1.5">{p.role}</p>
      {p.bio && <p className="text-navy mt-3 leading-relaxed">{p.bio}</p>}
    </Reveal>
  );
};

const Group = ({ title, people }) => {
  if (!people || people.length === 0) return null;
  return (
    <section className="py-16 md:py-20 border-t border-border/60">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="mb-12">
          <h2 className="font-serif text-3xl md:text-4xl text-navy">{title}</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {people.map((p, i) => <PersonCard key={p.name + i} p={p} i={i} />)}
        </div>
      </div>
    </section>
  );
};

const Family = () => {
  const { content } = useContent();
  const f = content?.family || {};
  const m = f.memorial || {};

  return (
    <div data-testid="family-page">
      <section className="pt-40 pb-14 md:pt-52 md:pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <span className="uppercase tracking-[0.3em] text-xs text-dusty font-bold">Our Family</span>
            <h1 className="font-serif text-5xl md:text-8xl text-navy tracking-tighter mt-4 leading-[0.95]">The ones we love</h1>
            <p className="text-navy text-lg md:text-xl max-w-2xl mt-8 leading-relaxed">{f.intro}</p>
          </Reveal>
        </div>
      </section>

      <Group title="Our Kids" people={f.kids} />
      <Group title="Siblings" people={f.siblings} />
      <Group title="Living Parents" people={f.livingParents} />

      {/* Memorial */}
      <section className="py-24 md:py-32 bg-secondary/40 mt-16" data-testid="memorial-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-12 max-w-2xl">
            <span className="uppercase tracking-[0.3em] text-xs text-dusty font-bold">Forever with us</span>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mt-4 italic">{m.title}</h2>
            {m.bodyHtml && <RichText html={m.bodyHtml} className="text-navy mt-6 text-lg" />}
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {(m.people || []).map((p, i) => <PersonCard key={p.name + i} p={p} i={i} />)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Family;
